// src/components/VenueCard.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/VenueCard.css';
import { pickFallback } from './fallbacks';
import { rescueEmoji } from '../utils/emojiRescue';
import { getVenueHoursState } from '../utils/venueDisplayHelpers';

// COMMUNITY_VIBE_UPGRADE_V1
const VIBE_OPTIONS = [
  { emoji: '🔥', label: "Let's Go" },
  { emoji: '😍', label: 'Love This' },
  { emoji: '🙂', label: 'Good Vibes' },
  { emoji: '😐', label: 'Meh' },
  { emoji: '💩', label: 'Not It' },
];

const emptyCounts = () => ({
  '🔥': 0,
  '😍': 0,
  '🙂': 0,
  '😐': 0,
  '💩': 0,
});

function legacyCommunitySummary(data = {}) {
  const counts = data?.counts || emptyCounts();
  const total = Number(data?.total ?? data?.totalVotes ?? 0) || 0;
  const topEmoji =
    data?.topEmoji ||
    Object.entries(counts).sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))[0]?.[0] ||
    null;

  return {
    primary: total > 0
      ? {
          concept: 'community_favorite',
          label: 'Community Check-In',
          emoji: topEmoji || '✨',
          total,
          updatedAt: null,
        }
      : {
          concept: 'community_favorite',
          label: 'Be the First to Rate',
          emoji: '✨',
          total: 0,
          updatedAt: null,
        },
    hotTonight: {
      concept: 'hot_tonight',
      label: 'No Ratings Tonight Yet',
      emoji: '🔥',
      total: 0,
      counts: emptyCounts(),
      updatedAt: null,
      windowHours: 12,
    },
    trending: {
      concept: 'trending',
      label: 'Building a Trend',
      icon: '✨',
      direction: 'none',
      recentRatings: 0,
      previousRatings: 0,
      delta: 0,
    },
    communityFavorite: {
      concept: 'community_favorite',
      label: total > 0 ? 'Community Check-In' : 'No Community Ratings Yet',
      emoji: topEmoji || '✨',
      total,
      counts,
      updatedAt: null,
    },
  };
}

function normalizeVibeSummary(data) {
  if (!data || typeof data !== 'object') return legacyCommunitySummary();
  if (data.communityVibe?.hotTonight) return data.communityVibe;
  if (data.hotTonight && data.trending && data.communityFavorite) return data;
  return legacyCommunitySummary(data);
}

function formatRelativeTime(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function formatRetryTime(ms) {
  const seconds = Math.max(1, Math.ceil(Number(ms || 0) / 1000));
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

const hash = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const VenueCard = ({
  venue,
  onMapClick,
  backendBase = '',
  partyUid,
  fallbackPool = [],
  onFeaturedImpression,
}) => {
    const cardRef = useRef(null);

    // real place id (only this should be used for vibes / localStorage)
    const placeId = venue?.place_id || venue?.placeId || venue?.id || '';
        const venueHours = getVenueHoursState(venue);
const isSearchContext = !!venue?.pinned;
    const isSponsored = Boolean(
      venue?.isSponsored ||
      venue?.isFeatured ||
      (Array.isArray(venue?.tags) &&
        venue.tags.some((tag) => /^(sponsored|featured)$/i.test(String(tag || '').trim())))
    );
    const featuredImpressionSentRef = useRef('');

    const isEasterEgg =
      venue?.easterEgg === true ||
      venue?.searchMode === 'blocked-easter-egg' ||
      venue?.hideVibeSystem === true;

    const easterEggScoreEmoji = venue?.partyScoreEmoji || venue?.emoji || '🚿';

    // stable key for *images* even if placeId is missing
    const imageKey = useMemo(() => {
        const name = String(venue?.name || '').trim();
        const lat =
        venue?.geometry?.location?.lat ??
        venue?.lat ??
        venue?.latitude ??
        null;
        const lng =
        venue?.geometry?.location?.lng ??
        venue?.lng ??
        venue?.longitude ??
        null;

        // prefer placeId when present
        if (placeId) return String(placeId);

        // otherwise name + coords (or just name)
        if (name && lat != null && lng != null) return `${name}@${lat},${lng}`;
        if (name) return name;

        // last resort
        return 'venue';
    }, [placeId, venue]);

    const stableFallback = useMemo(() => {
      const clean = Array.isArray(fallbackPool)
        ? fallbackPool
            .map((item) => {
              if (typeof item === 'string') return item.trim();

              if (item && typeof item === 'object') {
                return String(
                  item.imageUrl ||
                  item.url ||
                  item.src ||
                  ''
                ).trim();
              }

              return '';
            })
            .filter(Boolean)
        : [];

      const raw =
        clean.length > 0
          ? clean[hash(imageKey) % clean.length]
          : pickFallback(hash(imageKey) % 100);

      if (!raw) return null;

      if (
        raw.startsWith('http://') ||
        raw.startsWith('https://') ||
        raw.startsWith('data:') ||
        raw.startsWith('blob:')
      ) {
        return raw;
      }

      const base = String(backendBase || '').replace(/\/+$/, '');
      const imagePath = raw.startsWith('/') ? raw : `/${raw}`;

      return base ? `${base}${imagePath}` : imagePath;
    }, [fallbackPool, imageKey, backendBase]);

    // ---- Google photo ref detection ----
    const googleRef =
    venue?.photos?.[0]?.photo_reference ||
    venue?.photos?.[0]?.photoReference ||
    venue?.photos?.[0]?.ref ||
    venue?.photo_reference ||
    venue?.photoReference ||
    null;

    const googleImage = useMemo(() => {
        if (!googleRef || !backendBase) return null;
        return `${backendBase}/api/place-photo?ref=${encodeURIComponent(googleRef)}&maxwidth=400`;
    }, [backendBase, googleRef]);

    // ---- Backend image fields (but ignore server placeholder default-image) ----
    const backendArtistImage = useMemo(() => {
        const raw =
        (typeof venue?.image === 'string' && venue.image.trim()) ||
        (typeof venue?.artistImage === 'string' && venue.artistImage.trim()) ||
        (typeof venue?.takeoverImage === 'string' && venue.takeoverImage.trim()) ||
        (typeof venue?.imageUrl === 'string' && venue.imageUrl.trim()) ||
        (typeof venue?.photo === 'string' && venue.photo.trim()) ||
        '';

        if (!raw) return null;

        const lowered = raw.toLowerCase();

        // ignore bad/placeholder image values so fallbackPool can take over
        if (
          lowered === 'null' ||
          lowered === 'undefined' ||
          lowered.includes('/default-image.jpg') ||
          lowered.includes('undefined') ||
          lowered.includes('null') ||
          lowered.includes('chatgpt') ||
          lowered.includes('oaistatic') ||
          lowered.includes('.webp') ||
          lowered.includes('format=webp') ||
          lowered.includes('fm=webp') ||
          lowered.includes('auto=format')
        ) {
          return null;
        }

        if (
          raw.startsWith('http://') ||
          raw.startsWith('https://') ||
          raw.startsWith('data:') ||
          raw.startsWith('blob:')
        ) {
          return raw;
        }

        // These files are bundled inside the React/Capacitor app.
        // Do not turn them into Heroku URLs.
        if (
          raw.startsWith('/headline-pool/') ||
          raw.startsWith('headline-pool/') ||
          raw.startsWith('/static/') ||
          raw.startsWith('static/')
        ) {
          return raw.startsWith('/') ? raw : `/${raw}`;
        }

        // Backend-hosted artist images should use the Heroku origin.
        const base = (backendBase || '').replace(/\/+$/, '');
        const imagePath = raw.startsWith('/') ? raw : `/${raw}`;

        return base ? `${base}${imagePath}` : imagePath;
    }, [
        backendBase,
        venue?.image,
        venue?.artistImage,
        venue?.takeoverImage,
        venue?.imageUrl,
        venue?.photo,
    ]);


    // ---- Priority: backend artist image > Google proxy > stable fallback ----
    const imageSrc = backendArtistImage || googleImage || stableFallback;

    const [vibeSummary, setVibeSummary] = useState(() =>
      normalizeVibeSummary(
        venue?.communityVibe || {
          total: venue?.vibeTotal || 0,
          topEmoji: venue?.vibeTopEmoji || null,
          counts: venue?.vibeCounts || null,
        }
      )
    );
    const [showScore, setShowScore] = useState(false);
    const [ratingBusy, setRatingBusy] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
      setVibeSummary(
        normalizeVibeSummary(
          venue?.communityVibe || {
            total: venue?.vibeTotal || 0,
            topEmoji: venue?.vibeTopEmoji || null,
            counts: venue?.vibeCounts || null,
          }
        )
      );
    }, [venue?.communityVibe, venue?.vibeTotal, venue?.vibeTopEmoji, venue?.vibeCounts]);

    useEffect(() => {
      if (!toast) return undefined;
      const expiresAt = toast.expiresAt ? new Date(toast.expiresAt).getTime() : 0;
      const delay = toast.canUndo && expiresAt
        ? Math.max(1000, expiresAt - Date.now())
        : 5000;
      const timer = setTimeout(() => setToast(null), delay);
      return () => clearTimeout(timer);
    }, [toast]);

    useEffect(() => {
      if (!isSponsored || !placeId || typeof onFeaturedImpression !== 'function') return;

      const impressionKey = String(placeId);
      if (featuredImpressionSentRef.current === impressionKey) return;

      featuredImpressionSentRef.current = impressionKey;
      onFeaturedImpression(venue);
    }, [isSponsored, placeId, onFeaturedImpression, venue]);

    const [myVibe, setMyVibe] = useState(() => {
      try {
        return placeId ? localStorage.getItem(`vibe_${placeId}`) : null;
      } catch {
        return null;
      }
    });

    useEffect(() => {
      try {
        setMyVibe(placeId ? localStorage.getItem(`vibe_${placeId}`) : null);
      } catch {
        setMyVibe(null);
      }
    }, [placeId]);

    const buildVibePayload = (emoji = null) => ({
      placeId,
      ...(emoji ? { emoji } : {}),
      venueName: String(venue?.name || '').trim(),
      category: String(
        venue?.category || venue?.searchCategory || venue?.type || ''
      ).trim(),
      neighborhood: String(
        venue?.neighborhood || venue?.requestedNeighborhood || ''
      ).trim(),
      vicinity: String(
        venue?.vicinity || venue?.formatted_address || ''
      ).trim(),
      googleRating: Number(venue?.rating) || null,
      googleReviewCount: Number(venue?.user_ratings_total) || 0,
      image: String(venue?.image || '').trim(),
      query: String(
        venue?.searchQuery || venue?.searchAttribution?.searchQuery || ''
      ).trim(),
      searchLabel: String(venue?.searchLabel || '').trim(),
      searchTraceId:
        venue?.searchTraceId || venue?.searchAttribution?.searchTraceId || '',
      searchSessionId:
        venue?.searchSessionId || venue?.searchAttribution?.searchSessionId || '',
      resultCount:
        venue?.searchResultCount ??
        venue?.searchAttribution?.searchResultCount ??
        null,
      resultRank:
        venue?.resultRank ?? venue?.searchAttribution?.resultRank ?? null,
      matchScore:
        venue?.matchScore ?? venue?.searchAttribution?.matchScore ?? null,
      matchConfidence:
        venue?.matchConfidence || venue?.searchAttribution?.matchConfidence || '',
      gateTier: venue?.gateTier || venue?.searchAttribution?.gateTier || '',
      broadeningMode:
        venue?.broadeningMode || venue?.searchAttribution?.broadeningMode || '',
      retrievalMode:
        venue?.retrievalMode || venue?.searchAttribution?.retrievalMode || '',
      retrievalSource:
        venue?.retrievalSource || venue?.searchAttribution?.retrievalSource || '',
      searchElapsedMs:
        venue?.searchElapsedMs ??
        venue?.searchAttribution?.searchElapsedMs ??
        null,
      isClosestResult:
        venue?.isClosestResult === true ||
        venue?.searchAttribution?.isClosestResult === true,
      isHighestRatedResult:
        venue?.isHighestRatedResult === true ||
        venue?.searchAttribution?.isHighestRatedResult === true,
      source: 'ios',
    });

    const showVibeError = (data, status) => {
      const message = data?.code === 'VIBE_COOLDOWN'
        ? `You can update this vibe in ${formatRetryTime(data?.retryAfterMs)}.`
        : data?.error || `Rating failed (${status}).`;
      setToast({ type: 'error', message, canUndo: false });
    };

    const sendVibe = async (emoji) => {
      if (!partyUid || !placeId || ratingBusy) return;
      const option = VIBE_OPTIONS.find((item) => item.emoji === emoji);
      setRatingBusy(true);

      try {
        const res = await fetch(`${backendBase}/api/vibes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Party-Uid': partyUid,
          },
          body: JSON.stringify(buildVibePayload(emoji)),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.ok) {
          showVibeError(data, res.status);
          return;
        }

        setMyVibe(emoji);
        try {
          localStorage.setItem(`vibe_${placeId}`, emoji);
        } catch {}
        setVibeSummary(normalizeVibeSummary(data));

        const changedMessage = data?.action === 'change'
          ? `Vibe updated to ${emoji} ${option?.label || ''}.`
          : `${emoji} ${option?.label || 'Vibe'} saved.`;
        setToast({
          type: 'success',
          message: changedMessage,
          canUndo: data?.interaction?.canUndo === true,
          expiresAt: data?.interaction?.correctionExpiresAt || null,
        });
      } catch (error) {
        console.error('Vibe error', error);
        setToast({
          type: 'error',
          message: 'The rating could not be saved. Check your connection and try again.',
          canUndo: false,
        });
      } finally {
        setRatingBusy(false);
      }
    };

    const undoVibe = async () => {
      if (!partyUid || !placeId || ratingBusy) return;
      setRatingBusy(true);

      try {
        const res = await fetch(
          `${backendBase}/api/vibes/${encodeURIComponent(placeId)}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-Party-Uid': partyUid,
            },
            body: JSON.stringify(buildVibePayload()),
          }
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.ok) {
          showVibeError(data, res.status);
          return;
        }

        setMyVibe(null);
        try {
          localStorage.removeItem(`vibe_${placeId}`);
        } catch {}
        setVibeSummary(normalizeVibeSummary(data));
        setToast({
          type: 'success',
          message: data?.undone === false ? 'No rating to undo.' : 'Rating undone.',
          canUndo: false,
        });
      } catch (error) {
        console.error('Vibe undo error', error);
        setToast({
          type: 'error',
          message: 'The rating could not be undone. Please try again.',
          canUndo: false,
        });
      } finally {
        setRatingBusy(false);
      }
    };

    const hotTonight = vibeSummary?.hotTonight || legacyCommunitySummary().hotTonight;
    const trending = vibeSummary?.trending || legacyCommunitySummary().trending;
    const communityFavorite =
      vibeSummary?.communityFavorite || legacyCommunitySummary().communityFavorite;
    const primaryVibe = vibeSummary?.primary || communityFavorite;
    const primaryMeta = primaryVibe?.concept === 'hot_tonight'
      ? `${hotTonight.total} recent rating${hotTonight.total === 1 ? '' : 's'}`
      : primaryVibe?.concept === 'trending'
        ? `${trending.recentRatings || 0} ratings in the last 7 days`
        : communityFavorite.total > 0
          ? `${communityFavorite.total} community rating${communityFavorite.total === 1 ? '' : 's'}`
          : 'Your rating helps everyone decide.';

    const googleRating = Number(venue?.rating);
    const googleReviewCount = Number(venue?.user_ratings_total || 0);

    useEffect(() => {
        const run = () => rescueEmoji(cardRef.current);

        run();
        const raf = requestAnimationFrame(run);
        const timer = setTimeout(run, 120);

        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [showScore, vibeSummary, toast, myVibe]);

    return (
            <div
              ref={cardRef}
              className={`venue-card ${isSearchContext ? 'search-context-card' : ''}`}
            >
            <img
            src={imageSrc}
            alt={venue?.name || 'Discover nearby'}
            className="venue-image"
            onError={(e) => {
              const el = e.currentTarget;

              if (!el.dataset.usedPrimaryFallback && stableFallback) {
                el.dataset.usedPrimaryFallback = '1';
                el.src = stableFallback;
                return;
              }

              if (!el.dataset.usedBundledFallback) {
                el.dataset.usedBundledFallback = '1';

                const bundledFallback =
                  pickFallback((hash(imageKey) + 17) % 100);

                if (bundledFallback) {
                  el.src = bundledFallback;
                  return;
                }
              }

              el.onerror = null;
              el.style.display = 'none';
            }}
            />

            <div className="venue-info">
            <h3 className="venue-name">{venue?.name}</h3>

            {!isSearchContext && (isSponsored || (Array.isArray(venue?.tags) && venue.tags.length > 0)) ? (
              <div className="venue-badges">
                {isSponsored ? (
                  <span
                    className="venue-badge sponsored-badge"
                    title="Paid placement"
                    style={{
                      background: 'rgba(255,45,179,0.14)',
                      border: '1px solid rgba(255,45,179,0.34)',
                    }}
                  >
                    Sponsored
                  </span>
                ) : null}

                {(Array.isArray(venue?.tags) ? venue.tags : [])
                  .filter((tag) => {
                    const value = String(tag || '').trim();
                    return value && !/^(sponsored|featured)$/i.test(value);
                  })
                  .map((tag, index) => (
                    <span key={`${tag}-${index}`} className="venue-badge">
                      {tag}
                    </span>
                  ))}
              </div>
            ) : null}

            {isSearchContext ? (
              <p className="emoji-rating">{venue?.emoji || '💩'}</p>
            ) : (
              <div className="vibe-block community-vibe-block">
                {isEasterEgg ? (
                  <>
                    <button
                      className="pb-score-btn"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowScore(true);
                      }}
                    >
                      Party Button Score ▸
                    </button>
                    {showScore && (
                      <div className="pb-score-detail">
                        <span className="pb-score-emoji" aria-label="Cold shower">
                          {easterEggScoreEmoji}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="community-vibe-hero">
                      <div className="community-vibe-hero-emoji">
                        {primaryVibe?.emoji || '✨'}
                      </div>
                      <div className="community-vibe-hero-copy">
                        <div className="community-vibe-hero-label">
                          {primaryVibe?.label || 'Be the First to Rate'}
                        </div>
                        <div className="community-vibe-hero-meta">
                          {primaryMeta}
                        </div>
                      </div>
                    </div>

                    <div className="community-vibe-concepts" aria-label="Party Button community signals">
                      <div className="community-vibe-concept">
                        <span className="community-vibe-concept-icon">
                          {hotTonight?.emoji || '🔥'}
                        </span>
                        <span className="community-vibe-concept-copy">
                          <strong>Hot Tonight</strong>
                          <small>
                            {hotTonight?.total > 0
                              ? `${hotTonight.total} recent`
                              : 'No ratings yet'}
                          </small>
                        </span>
                      </div>

                      <div className="community-vibe-concept">
                        <span className="community-vibe-concept-icon">
                          {trending?.icon || '✨'}
                        </span>
                        <span className="community-vibe-concept-copy">
                          <strong>Trending</strong>
                          <small>{trending?.label || 'Building a Trend'}</small>
                        </span>
                      </div>

                      <div className="community-vibe-concept">
                        <span className="community-vibe-concept-icon">
                          {communityFavorite?.emoji || '✨'}
                        </span>
                        <span className="community-vibe-concept-copy">
                          <strong>Community Favorite</strong>
                          <small>
                            {communityFavorite?.total > 0
                              ? `${communityFavorite.total} total`
                              : 'Be the first'}
                          </small>
                        </span>
                      </div>
                    </div>

                    <div className="google-rating-line">
                      Google:{' '}
                      {Number.isFinite(googleRating) && googleRating > 0
                        ? `${googleRating.toFixed(1)} ★`
                        : 'No rating'}
                      {googleReviewCount > 0
                        ? ` · ${googleReviewCount.toLocaleString()} reviews`
                        : ''}
                    </div>

                    <div className="vibe-rating-prompt">How is the vibe?</div>
                    <div className="vibe-bar labeled-vibe-bar">
                      {VIBE_OPTIONS.map((option) => (
                        <button
                          key={option.emoji}
                          className={`vibe-button labeled-vibe-button ${myVibe === option.emoji ? 'selected' : ''}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            sendVibe(option.emoji);
                          }}
                          type="button"
                          disabled={!placeId || ratingBusy}
                          title={!placeId ? 'Vibes unavailable for this result' : option.label}
                          aria-label={`${option.emoji} ${option.label}`}
                        >
                          <span className="vibe-button-emoji">{option.emoji}</span>
                          <span className="vibe-button-label">{option.label}</span>
                        </button>
                      ))}
                    </div>

                    {toast && (
                      <div
                        className={`vibe-toast ${toast.type === 'error' ? 'is-error' : 'is-success'}`}
                        role="status"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <span className="vibe-toast-message">{toast.message}</span>
                        {toast.canUndo && (
                          <button
                            className="vibe-toast-undo"
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              undoVibe();
                            }}
                            disabled={ratingBusy}
                          >
                            Undo
                          </button>
                        )}
                        <button
                          className="vibe-toast-close"
                          type="button"
                          aria-label="Dismiss message"
                          onClick={(event) => {
                            event.stopPropagation();
                            setToast(null);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {isSearchContext && (
                                 <p className="search-context-subtext">
                                 Showing venues within this search radius
                                 </p>
                                 )}

            {!isSearchContext && (
                                  <>
                                  <p className={venueHours.className}>
                                    {venueHours.label}
                                  </p>

                                  {venue?.price_level && (
                                                          <p className="venue-price">Price: {'$'.repeat(venue.price_level)}</p>
                                                          )}

                                  {venue?.walkingTime && <p className="venue-walk">Walk: {venue.walkingTime}</p>}
                                  </>
                                  )}

            <div className="venue-card-buttons">
            <button
              className="venue-button map-button"
              onClick={() => onMapClick?.(venue, { isSearchContext })}
              type="button"
            >
            📍 Map
            </button>
            </div>
            </div>
            </div>
            );

};

export default VenueCard;
